import { createApp } from 'vue';
import { getFluxIbeUrl } from '@utils/utils';
import type { HeaderProps } from '@lmt-rpb/ComponentHeader/componentHeaderTypes';
import type { ApiDataTypes } from '@components/common/types';
import { parseSearchMaskAndFilterSettings, parsedDataToQuery } from '@components/common/services/transform';
import HotelGrid from './HotelGrid.vue';
import { extractTypoDataInfo } from '../common/services/typoSearchParamService';
import { mountReplace } from '@/js/utils/mountUtils';
import type { ItemType } from '@/interfaces/hotel-list-types/hotel-list-types';
import { HotelTransformService } from './hotelTransformService';

interface GridSettings {
	isSlider: boolean;
	fourColumns: boolean;
	headerProps: HeaderProps;
	moreLink: string;
}

interface HotelGridState {
	hotelGridOffers: ItemType[];
	hotelgridParams: ApiDataTypes | Record<string, never>;
	headerProps: HeaderProps;
	apiUrl: string;
	fourColumns: boolean;
	queryString: string;
	isSlider: boolean;
	moreLink: string;
	hotelOnly: boolean;
	hotelBoxAmount: number;
}

export class HotelGridService {
	private state: HotelGridState = {
		hotelGridOffers: [],
		hotelgridParams: {},
		headerProps: {} as HeaderProps,
		apiUrl: '',
		fourColumns: false,
		queryString: '',
		isSlider: true,
		moreLink: '',
		hotelOnly: false,
		hotelBoxAmount: 0,
	};

	private initializeGridSettings(gridSection: HTMLElement, parentElement: HTMLElement): GridSettings {
		const gridSectionData = gridSection.dataset;
		const headers = parentElement?.dataset;

		return {
			isSlider: gridSectionData?.slider === 'true',
			fourColumns: parentElement?.className.includes('--4columns'),
			headerProps: this.createHeaderProps(headers),
			moreLink: headers?.moreLink ?? '',
		};
	}

	private createHeaderProps(headers: DOMStringMap = {}): HeaderProps {
		return {
			title: headers.header ?? '',
			subtitle: headers.subheader ?? '',
			mobileTitle: headers.headerMobile ?? '',
			mobileSubtitle: headers.subheaderMobile ?? '',
			moreText: headers.linkText ?? 'Mehr',
			link: headers.moreLink,
			titleAttribute: headers.headerMobile ?? headers.header ?? '',
		};
	}

	private processHotelBoxData(element: HTMLElement, isAutoGeneratedTopic: boolean): void {
		const data = element.dataset;
		const perBoxPluginHotelOnly = data?.hotelOnly === 'true';
		const parsedParams = data.hotelboxParams && !isAutoGeneratedTopic ? JSON.parse(data.hotelboxParams) : {};
		const perBoxPluginParams = data.hotelboxParams && !isAutoGeneratedTopic ? parsedParams : false;

		this.state.hotelBoxAmount += data.limit ? Number(data.limit) : 0;

		if (data.hotelboxResponse) {
			const offers: ItemType[] = JSON.parse(data.hotelboxResponse).Offers;
			if (offers) {
				if (perBoxPluginParams) {
					offers.forEach((offer) => {
						offer.TravellerAmount = {
							Adults: parsedParams.Adults ?? 2,
							Children: parsedParams.Children ?? [],
						};
						offer.Hotel.URL = HotelTransformService.transformUrl(offer, perBoxPluginParams, perBoxPluginHotelOnly);
					});
				}
				if (isAutoGeneratedTopic) {
					this.updateOffersForAutoGeneratedTopic(data, offers);
				}
				this.state.hotelGridOffers.push(...offers);
			}
		}
	}

	private handlePluginData(
		gridSection: HTMLElement,
		parentElement: HTMLElement,
		dataElements: NodeListOf<Element>,
		isAutoGeneratedTopic: boolean
	): void {
		const settings = this.initializeGridSettings(gridSection, parentElement);
		Object.assign(this.state, settings);
		if (!dataElements.length) {
			console.log('HotelBoxen: Keine Hoteldaten verfügbar.');
			return;
		}

		dataElements.forEach((element) => {
			this.processHotelBoxData(element as HTMLElement, isAutoGeneratedTopic);
		});
	}

	private handleNonPluginData(gridSection: HTMLElement): void {
		const dataElement = gridSection.querySelector('.rpb_hotelgrid--data') as HTMLElement;
		const gridData = dataElement?.dataset;

		if (!gridData) {
			console.error('HotelGrid: Keine Daten für Request verfügbar.');
			return;
		}
		const destinationName = gridData.destinationName ?? '';
		const parsedParams = gridData.hotelgridParams ? JSON.parse(gridData.hotelgridParams) : {};

		Object.assign(this.state, {
			hotelgridParams: {
				...parsedParams,
				Adults: parsedParams.Adults ?? 2,
				Children: parsedParams.Children ?? [],
			},
			hotelOnly: gridData.hotelOnly === 'true',
			hotelBoxAmount: gridData.limit ? Number(gridData.limit) : 3,
			headerProps: {
				title: `Empfehlungen für ${destinationName} Urlaub`,
				subtitle: gridData.subHeadline || 'Beliebte Hotels mit Bestpreis-Garantie',
				mobileTitle: `Empfehlungen für ${destinationName} Urlaub`,
				mobileSubtitle: gridData.subHeadlineMobile ?? '',
				moreText: 'Mehr',
				link: '',
				titleAttribute: `${destinationName} Hotels`,
			},
		});
	}

	private updateOffersForAutoGeneratedTopic(data: DOMStringMap, offers: ItemType[]): void {
		this.state.headerProps.titleAttribute = `${data.destinationName} Hotels`;

		const urlParamsData = (document.querySelector('.url-params-data') as HTMLElement)?.dataset;
		const searchMaskData = extractTypoDataInfo('#search-mask-data');

		if (urlParamsData && searchMaskData) {
			const parsedUrlParams = parseSearchMaskAndFilterSettings(urlParamsData).urlParams;
			this.state.queryString = parsedDataToQuery({ ...searchMaskData, ...parsedUrlParams });

			offers.forEach((offer) => {
				offer.Hotel.URL = this.state.queryString ? `${offer.Hotel.URL}?${this.state.queryString}` : offer.Hotel.URL;
			});
		}
	}

	private getApiUrl(): string {
		const apiEndPoint = '/search/best-offer/hotel';
		return `${getFluxIbeUrl()}${apiEndPoint}`;
	}

	private mountHotelGrid(selector: string): void {
		const mountPoint = document.querySelector(selector);
		if (!mountPoint) return;

		const { hotelgridParams, ...restState } = this.state;
		const app = createApp(HotelGrid, {
			...restState,
			bodyParams: hotelgridParams,
			uniqueKey: selector,
			fromPlugin: !!this.state.moreLink,
			isAutoGeneratedTopic: !!this.state.queryString,
		});

		if (mountPoint) {
			mountReplace(app, mountPoint);
		}
	}

	public initHotelGrid(selector: string): void {
		const gridSection = document.querySelector(selector) as HTMLElement;
		const parentElement = gridSection.parentNode as HTMLElement;
		const dataElements = parentElement?.querySelectorAll('.rpb_hotelgrid--box-data');
		const fromPlugin = !!parentElement?.querySelector('.rpb_hotelgrid--box-data[data-from-plugin="true"]');
		const isAutoGeneratedTopic = !!parentElement?.querySelector('.rpb_hotelgrid--box-data[data-is-auto-generated-topic="true"]');
		if (fromPlugin) {
			this.handlePluginData(gridSection, parentElement, dataElements, isAutoGeneratedTopic);
		} else {
			this.handleNonPluginData(gridSection);
		}

		if (fromPlugin && !this.state.hotelGridOffers.length) {
			console.error('HotelGrid: Keine Hoteldaten verfügbar.');
			parentElement?.remove();
			return;
		}

		this.state.apiUrl = this.getApiUrl();
		this.mountHotelGrid(selector);

		if (fromPlugin) {
			dataElements.forEach((element) => element.remove());
		}
	}
}
